import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'gs_theme';
const DARK = 'dark';
const LIGHT = 'light';

const ThemeContext = createContext({
  theme: DARK,
  isDark: true,
  setTheme: () => {},
  toggleTheme: () => {}
});

function readInitialTheme() {
  if (typeof window === 'undefined') return DARK;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === LIGHT || stored === DARK) return stored;
  } catch (error) {
    // Continue with the system preference when storage is unavailable.
  }
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? LIGHT : DARK;
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;

  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme === LIGHT ? '#F2F5FC' : '#07081f');
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);
  const transitionTimerRef = useRef(null);

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useLayoutEffect(() => () => {
    if (transitionTimerRef.current) window.clearTimeout(transitionTimerRef.current);
    document.documentElement.classList.remove('theme-transition');
  }, []);

  const savePreference = useCallback((nextTheme) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, nextTheme);
    } catch (error) {
      // The theme still works for this tab if storage is unavailable.
    }
  }, []);

  const beginThemeTransition = useCallback(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const root = document.documentElement;
    root.classList.add('theme-transition');
    if (transitionTimerRef.current) window.clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = window.setTimeout(() => {
      root.classList.remove('theme-transition');
      transitionTimerRef.current = null;
    }, 350);
  }, []);

  const setTheme = useCallback((nextTheme) => {
    const normalizedTheme = nextTheme === LIGHT ? LIGHT : DARK;
    beginThemeTransition();
    setThemeState(normalizedTheme);
    savePreference(normalizedTheme);
  }, [beginThemeTransition, savePreference]);

  const toggleTheme = useCallback(() => {
    const nextTheme = theme === DARK ? LIGHT : DARK;
    beginThemeTransition();
    setThemeState(nextTheme);
    savePreference(nextTheme);
  }, [theme, beginThemeTransition, savePreference]);

  const value = useMemo(() => ({
    theme,
    isDark: theme === DARK,
    setTheme,
    toggleTheme
  }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export { DARK as DARK_THEME, LIGHT as LIGHT_THEME, STORAGE_KEY as THEME_STORAGE_KEY };
export default ThemeContext;
